import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";
// color design tokens export

const FONT_STACK = ["Source Sans 3", "sans-serif"].join(",");

// Two tiers per mode: `rest` is what a card carries by default, `hover` is the bloom it
// grows into. Exported because hoverLift() in src/styles/surfaces.js needs the same pair,
// and a card whose hover shadow does not match its resting one looks like it changed
// material rather than height.
export const SHADOW = {
  dark: {
    rest: "0 1px 2px rgba(0, 0, 0, 0.28), 0 4px 12px -4px rgba(0, 0, 0, 0.35)",
    hover: "0 2px 4px rgba(0, 0, 0, 0.32), 0 14px 28px -8px rgba(0, 0, 0, 0.5)",
  },
  light: {
    rest: "0 1px 2px rgba(16, 24, 40, 0.05), 0 4px 12px -4px rgba(16, 24, 40, 0.08)",
    hover: "0 2px 4px rgba(16, 24, 40, 0.06), 0 14px 28px -8px rgba(16, 24, 40, 0.16)",
  },
};

// New color palette based on modern construction/architecture theme
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#ffffff",
          200: "#f8f9fa",
          300: "#e9ecef",
          400: "#dee2e6",
          500: "#adb5bd",
          600: "#6c757d",
          700: "#495057",
          800: "#343a40",
          900: "#212529"
        },
        primary: {
          100: "#e9f2fc",
          200: "#cbe0f7",
          300: "#a9caf0",
          400: "#79aee7",
          500: "#5fa2e4",
          600: "#4d97e0",
          700: "#3a83cc",
          800: "#2e6cae",
          900: "#235690"
        },
        secondary: {
          100: "#fef6e7",
          200: "#fdecd0",
          300: "#fbe3b9",
          400: "#fad9a1",
          500: "#f9d08a",
          600: "#f8c673",
          700: "#f7bc5c",
          800: "#f6b344",
          900: "#f5a92d"
        },
        background: {
          100: "#1a1f2e",
          200: "#232936",
          300: "#2b323f",
          400: "#343b47",
          500: "#3c4450",
          600: "#454d59",
          700: "#4d5661",
          800: "#565f6a",
          900: "#5e6873"
        },
        success: {
          100: "#e8f5e9",
          200: "#c8e6c9",
          300: "#a5d6a7",
          400: "#81c784",
          500: "#66bb6a",
          600: "#4caf50",
          700: "#43a047",
          800: "#388e3c",
          900: "#2e7d32"
        },
        error: {
          100: "#ffebee",
          200: "#ffcdd2",
          300: "#ef9a9a",
          400: "#e57373",
          500: "#ef5350",
          600: "#f44336",
          700: "#e53935",
          800: "#d32f2f",
          900: "#c62828"
        }
      }
    : {
        grey: {
          100: "#212529",
          200: "#343a40",
          300: "#495057",
          400: "#6c757d",
          500: "#adb5bd",
          600: "#dee2e6",
          700: "#e9ecef",
          800: "#f8f9fa",
          900: "#ffffff"
        },
        primary: {
          100: "#114b80",
          200: "#14568f",
          300: "#1a6cb0",
          400: "#2f84cc",
          500: "#4d97e0",
          600: "#79aee7",
          700: "#a9caf0",
          800: "#cbe0f7",
          900: "#e9f2fc"
        },
        secondary: {
          100: "#f5a92d",
          200: "#f6b344",
          300: "#f7bc5c",
          400: "#f8c673",
          500: "#f9d08a",
          600: "#fad9a1",
          700: "#fbe3b9",
          800: "#fdecd0",
          900: "#fef6e7"
        },
        // Deliberately off-white. 200 is the page canvas and 300 the raised surfaces, and
        // the gap between them is the only thing separating a card from its band — the
        // MuiCard shadow alone was not carrying it. Pulling the canvas a little off pure
        // white and the surfaces further down again widens that gap from 7 points to 12.
        // If you retune 200, update the pre-paint layer in public/index.html to match.
        background: {
          100: "#fafbfc",
          200: "#f2f4f6",
          300: "#e6eaee",
          400: "#dae0e6",
          500: "#ced5dd",
          600: "#bfc8d1",
          700: "#9aa5b1",
          800: "#66707b",
          900: "#434c56"
        },
        success: {
          100: "#2e7d32",
          200: "#388e3c",
          300: "#43a047",
          400: "#4caf50",
          500: "#66bb6a",
          600: "#81c784",
          700: "#a5d6a7",
          800: "#c8e6c9",
          900: "#e8f5e9"
        },
        error: {
          100: "#c62828",
          200: "#d32f2f",
          300: "#e53935",
          400: "#f44336",
          500: "#ef5350",
          600: "#e57373",
          700: "#ef9a9a",
          800: "#ffcdd2",
          900: "#ffebee"
        }
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[600],
              light: colors.primary[400],
              dark: colors.primary[800],
            },
            secondary: {
              main: colors.secondary[600],
              light: colors.secondary[400],
              dark: colors.secondary[800],
            },
            background: {
              default: colors.background[200],
              paper: colors.background[300],
            },
            text: {
              primary: colors.grey[100],
              secondary: colors.grey[300],
              disabled: colors.grey[500],
            },
            error: {
              main: colors.error[600],
              light: colors.error[400],
              dark: colors.error[800],
            },
            success: {
              main: colors.success[600],
              light: colors.success[400],
              dark: colors.success[800],
            },
            divider: colors.grey[700],
          }
        : {
            // The light-mode ramps run in the opposite direction to the dark ones: here a
            // HIGHER index is a LIGHTER colour. So `light` takes the index above `main` and
            // `dark` the index below it — the reverse of the mapping in the branch above.
            primary: {
              main: colors.primary[300],
              light: colors.primary[500],
              dark: colors.primary[100],
            },
            secondary: {
              main: colors.secondary[400],
              light: colors.secondary[600],
              dark: colors.secondary[200],
            },
            background: {
              default: colors.background[200],
              paper: colors.background[100],
            },
            text: {
              primary: colors.grey[100],
              secondary: colors.grey[300],
              disabled: colors.grey[500],
            },
            error: {
              main: colors.error[400],
              light: colors.error[600],
              dark: colors.error[200],
            },
            success: {
              main: colors.success[400],
              light: colors.success[600],
              dark: colors.success[200],
            },
            // Not grey[300]. Because the light ramp is inverted, grey[300] evaluates to
            // the same #495057 the dark mode uses — which reads as a soft rule on a dark
            // canvas but a hard near-black hairline on a light one. A background-ramp
            // step is the right weight for a border here.
            divider: colors.background[500],
          }),
    },
    typography: {
      fontFamily: FONT_STACK,
      fontSize: 16,
      // The headings size fluidly rather than stepping at the `md` breakpoint. Every page
      // used to carry its own `fontSize: { xs, md }` override to work around the fixed px
      // sizes here, so the scale was really defined in eight places at once and jumped at
      // 900px. clamp() puts it back in one place and scales smoothly in between.
      h1: {
        fontFamily: FONT_STACK,
        fontSize: "clamp(2.25rem, 1.55rem + 3.1vw, 3.5rem)",
        fontWeight: 800,
        lineHeight: 1.1,
        letterSpacing: "-0.03em",
      },
      h2: {
        fontFamily: FONT_STACK,
        fontSize: "clamp(1.75rem, 1.3rem + 2vw, 2.5rem)",
        fontWeight: 800,
        lineHeight: 1.15,
        letterSpacing: "-0.025em",
      },
      h3: {
        fontFamily: FONT_STACK,
        fontSize: "clamp(1.5rem, 1.25rem + 1.1vw, 2rem)",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
      },
      h4: {
        fontFamily: FONT_STACK,
        fontSize: "clamp(1.25rem, 1.13rem + 0.55vw, 1.5rem)",
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: "-0.015em",
      },
      h5: {
        fontFamily: FONT_STACK,
        fontSize: "clamp(1.125rem, 1.07rem + 0.28vw, 1.25rem)",
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: "-0.01em",
      },
      h6: {
        fontFamily: FONT_STACK,
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: "1.0625rem",
        lineHeight: 1.65,
      },
      body2: {
        fontSize: "0.9375rem",
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 0,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Outside the legal pages, which rolled their own, keyboard users had no focus
          // indicator at all. One rule covers the whole site.
          "*:focus-visible": {
            outline: `2px solid ${mode === "dark" ? colors.primary[500] : colors.primary[300]}`,
            outlineOffset: "2px",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'background-color .2s ease, border-color .2s ease, color .2s ease',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            // Hairline border as well as the shadow, not shadow alone. The light canvas
            // and surface are only 12 points apart, so the edge is what actually defines
            // a card there; the shadow only seats it.
            border: `1px solid ${colors.background[400]}`,
            boxShadow: SHADOW[mode].rest,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

const COLOR_MODE_STORAGE_KEY = "av-facades-color-mode";

// Storage access throws outright when the browser blocks it (Safari private browsing,
// cookies disabled), so reads and writes are both guarded. A failure only means the
// preference will not stick — it must never stop the site rendering.
const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
};

const storeMode = (mode) => {
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch {
    // Preference simply will not persist for this visitor.
  }
};

export const useMode = () => {
  // Storage holds an explicit choice only, so it wins outright. Everyone else gets dark:
  // the site is designed dark-first, so that is the intended first impression regardless
  // of what the visitor's OS prefers.
  const [mode, setMode] = useState(() => readStoredMode() ?? "dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === "light" ? "dark" : "light";
          storeMode(next);
          return next;
        }),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  // Keeps the pre-paint layer in public/index.html in step with the live theme: the data
  // attribute selects which of its two body rules applies, and theme-color drives the
  // mobile address bar, which would otherwise sit on the old colour until the next
  // reload. Read off the theme so these stay derived rather than a third copy of the hexes.
  useEffect(() => {
    document.documentElement.setAttribute("data-color-mode", mode);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme.palette.background.default);
  }, [mode, theme]);

  return [theme, colorMode];
};